import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { computeMeta, fallbackMeta } from "@/utils/status";
import { useAuth } from "@/context/AuthContext";

const OutcomesContext = createContext({
  outcomes: [],
  results: [],
  statusMeta: {},
  statusesFor: () => [],
  metaFor: () => fallbackMeta(),
  resultsFor: () => [],
  resultMetaFor: () => fallbackMeta(),
  isCompletedStatus: () => false,
  refresh: async () => {},
  ready: false,
});

export function OutcomesProvider({ children }) {
  const { user } = useAuth();
  const [outcomes, setOutcomes] = useState([]);
  const [results, setResults] = useState([]);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setOutcomes([]); setResults([]); setReady(false); return; }
    try {
      const [o, r] = await Promise.all([
        api.get("/outcome-types"),
        api.get("/result-types"),
      ]);
      setOutcomes(o.data);
      setResults(r.data);
      setReady(true);
    } catch (_) {
      setReady(true);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const statusMeta = useMemo(() => {
    const map = {};
    outcomes.forEach((o) => { map[o.key] = computeMeta(o); });
    return map;
  }, [outcomes]);

  const resultMeta = useMemo(() => {
    const map = {};
    results.forEach((r) => { map[`${r.calendar}::${r.key}`] = computeMeta({ ...r, is_completed: true, is_default: false }); });
    return map;
  }, [results]);

  const statusesFor = useCallback(() => {
    const list = [...outcomes].sort((a, b) => a.order - b.order).map((o) => o.key);
    if (!list.length) return ["da_svolgere", "effettuato", "no_show", "annullato", "riprogrammato"];
    return list;
  }, [outcomes]);

  const resultsFor = useCallback((calendar) => {
    return results
      .filter((r) => r.calendar === calendar)
      .sort((a, b) => a.order - b.order)
      .map((r) => r.key);
  }, [results]);

  const metaFor = useCallback((key) => statusMeta[key] || fallbackMeta(key), [statusMeta]);

  const resultMetaFor = useCallback((calendar, key) => {
    if (!key) return fallbackMeta();
    return resultMeta[`${calendar}::${key}`] || fallbackMeta(key);
  }, [resultMeta]);

  const isCompletedStatus = useCallback((key) => {
    const found = outcomes.find((o) => o.key === key);
    return !!(found && found.is_completed);
  }, [outcomes]);

  const value = useMemo(() => ({
    outcomes, results, statusMeta,
    statusesFor, metaFor, resultsFor, resultMetaFor, isCompletedStatus,
    refresh, ready,
  }), [outcomes, results, statusMeta, statusesFor, metaFor, resultsFor, resultMetaFor, isCompletedStatus, refresh, ready]);

  return <OutcomesContext.Provider value={value}>{children}</OutcomesContext.Provider>;
}

export const useOutcomes = () => useContext(OutcomesContext);
